import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  Shield, 
  Zap, 
  Building2, 
  Globe,
  ChevronRight,
  Lock,
  TrendingUp,
  CheckCircle,
  Mail,
  Phone,
  Send
} from 'lucide-react';
import Lenis from 'lenis';

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: 'easeOut' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 25 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// Trust logos - Actual compliance targets (not yet achieved)
const trustLogos = [
  { name: 'Made in India', text: 'Built for Indian Enterprises' },
  { name: 'Secure', text: 'Enterprise Security' },
  { name: 'Encrypted', text: 'Data Encryption' },
  { name: 'Compliant', text: 'Regulatory Ready' },
];

export const LandingPagePro = () => {
  const containerRef = useRef(null);
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // Initialize Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/inquiries/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          phone: formData.phone || null,
          source: 'landing_page'
        }),
      });
      
      if (response.ok) {
        toast.success('Thank you! Our team will contact you shortly.');
        setFormData({ name: '', email: '', company: '', phone: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      toast.error('Failed to submit. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-[#F8FAFC] font-inter">
      {/* Navigation */}
      <motion.nav 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 rounded-xl bg-[#0A192F] flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              <span className="font-playfair text-2xl font-bold text-[#0A192F]">FinOrchest</span>
            </a>
            
            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-[#0A192F] transition-colors" data-testid="nav-features">
                Solutions
              </a>
              <a href="#future" className="text-sm font-medium text-slate-600 hover:text-[#0A192F] transition-colors" data-testid="nav-future">
                Future Ready
              </a>
              <a href="#contact" className="text-sm font-medium text-slate-600 hover:text-[#0A192F] transition-colors" data-testid="nav-contact">
                Partner With Us
              </a>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex items-center gap-4">
              <a href="/login" className="hidden sm:inline-flex text-sm font-medium text-slate-600 hover:text-[#0A192F]" data-testid="nav-signin">
                Sign In
              </a>
              <a 
                href="#contact"
                className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#2563EB] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                data-testid="nav-cta"
              >
                Request Demo
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background Elements */}
        <motion.div style={{ y, opacity }} className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-50 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-amber-50/50 to-transparent rounded-full blur-3xl" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Badge */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center gap-2 bg-[#0A192F]/5 border border-[#0A192F]/10 rounded-full px-4 py-2 mb-8"
              >
                <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
                <span className="text-sm font-medium text-[#0A192F]">Digital Rupee Ready</span>
              </motion.div>

              {/* Headline */}
              <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#0A192F] leading-[1.1] tracking-tight mb-6">
                Enterprise Fund<br />
                <span className="text-[#2563EB]">Management</span><br />
                Reimagined
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
                The next generation platform for businesses to manage organizational finances 
                with complete control, governance, and transparency.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a 
                  href="#contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#2563EB] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                  data-testid="hero-cta-primary"
                >
                  Schedule Partnership Discussion
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a 
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 text-[#0A192F] px-8 py-4 rounded-full text-base font-medium hover:bg-slate-50 hover:border-slate-300 transition-all duration-300"
                  data-testid="hero-cta-secondary"
                >
                  Learn More
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 flex-wrap">
                <span className="text-sm text-slate-500">Designed for:</span>
                <div className="flex items-center gap-4">
                  {['Banks', 'Enterprises', 'Government'].map((item) => (
                    <span key={item} className="text-sm font-medium text-slate-700 bg-slate-100 px-3 py-1 rounded-full">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right: Visual */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Abstract architectural image */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80" 
                    alt="Modern corporate architecture"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/40 to-transparent" />
                </div>
                
                {/* Floating card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-slate-100"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Enterprise Security</p>
                      <p className="font-semibold text-[#0A192F]">Encrypted & Secure</p>
                    </div>
                  </div>
                </motion.div>

                {/* Stats card */}
                <motion.div 
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute -top-4 -right-4 bg-[#0A192F] text-white rounded-2xl p-5 shadow-xl"
                >
                  <p className="font-mono text-3xl font-bold text-[#D4AF37]">e₹</p>
                  <p className="text-sm text-slate-300 mt-1">CBDC Ready</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Bar - Marquee */}
      <section className="py-12 border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-center gap-12 flex-wrap">
            <span className="text-sm text-slate-400 uppercase tracking-wider">Built With</span>
            <div className="flex items-center gap-8 md:gap-16">
              {trustLogos.map((logo, i) => (
                <motion.div 
                  key={logo.name}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 0.6 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-slate-400 hover:text-slate-600 transition-colors cursor-default"
                >
                  <span className="font-semibold text-lg">{logo.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Bento Grid */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-sm font-medium text-[#2563EB] uppercase tracking-wider">Platform Capabilities</span>
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] mt-4 mb-6">
              Built for Scale. Designed for Trust.
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A comprehensive solution for organizations seeking complete financial control and governance.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {/* Feature Cards */}
            {[
              {
                icon: Building2,
                title: 'Departmental Control',
                description: 'Allocate and monitor budgets across organizational units with precision.',
                color: 'blue'
              },
              {
                icon: Lock,
                title: 'Governance & Compliance',
                description: 'Multi-level approval workflows with complete audit trails.',
                color: 'emerald'
              },
              {
                icon: TrendingUp,
                title: 'Real-Time Visibility',
                description: 'Track organizational cash flows and spending patterns instantly.',
                color: 'amber'
              },
              {
                icon: Shield,
                title: 'Enterprise Security',
                description: 'Bank-grade encryption and security protocols throughout.',
                color: 'violet'
              },
              {
                icon: Globe,
                title: 'API-First Architecture',
                description: 'Seamless integration with existing banking and ERP systems.',
                color: 'rose'
              },
              {
                icon: Zap,
                title: 'Programmable Controls',
                description: 'Future-ready for CBDC and programmable money capabilities.',
                color: 'cyan'
              }
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                variants={staggerItem}
                className="group bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:border-slate-200 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-xl bg-${feature.color}-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-7 h-7 text-${feature.color}-600`} />
                </div>
                <h3 className="font-semibold text-xl text-[#0A192F] mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CBDC Section - Dark Mode */}
      <section id="future" className="py-24 md:py-32 bg-[#0A192F] relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4AF37]/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div {...fadeUp}>
              <span className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-4 py-2 mb-8">
                <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                <span className="text-sm font-medium text-[#D4AF37]">Future Ready</span>
              </span>
              
              <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Prepared for India's<br />
                <span className="text-[#D4AF37]">Digital Rupee</span>
              </h2>
              
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                FinOrchest is architected to seamlessly integrate with India's emerging CBDC infrastructure. 
                Our platform is designed to leverage programmable money capabilities when they become available.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  'RBI CBDC Sandbox Application Ready',
                  'Programmable Wallet Architecture',
                  'Smart Contract Compatible Design',
                  'Future-Proof Technology Stack'
                ].map((item, i) => (
                  <motion.div 
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <span className="text-slate-200">{item}</span>
                  </motion.div>
                ))}
              </div>

              <a 
                href="#contact"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-8 py-4 rounded-full text-base font-semibold hover:bg-[#e5c04a] transition-all duration-300 shadow-lg hover:shadow-xl"
                data-testid="cbdc-cta"
              >
                Discuss CBDC Partnership
                <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>

            {/* Visual */}
            <motion.div 
              {...fadeUp}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1580723588851-b365dc5682ea?w=700&q=80" 
                  alt="Digital future"
                  className="w-full h-[400px] lg:h-[500px] object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-transparent to-[#0A192F]/50" />
              </div>
              
              {/* Floating element */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="absolute bottom-6 left-6 right-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6"
              >
                <p className="font-mono text-4xl font-bold text-[#D4AF37] mb-2">e₹</p>
                <p className="text-sm text-white/80">
                  When CBDC goes enterprise, we'll be ready. Will you?
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left: Info */}
            <motion.div {...fadeUp}>
              <span className="text-sm font-medium text-[#2563EB] uppercase tracking-wider">Partner With Us</span>
              <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] mt-4 mb-6">
                Let's Build Together
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                We're seeking strategic partnerships with forward-thinking banks and financial institutions. 
                Let's discuss how FinOrchest can add value to your enterprise banking offerings.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A192F] mb-1">For Banks</h4>
                    <p className="text-slate-600">Explore integration opportunities and white-label solutions.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A192F] mb-1">For Enterprises</h4>
                    <p className="text-slate-600">Transform your organizational fund management.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A192F] mb-1">Contact</h4>
                    <p className="text-slate-600">partnerships@finorchest.com</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Form */}
            <motion.div 
              {...fadeUp}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl border border-slate-100"
            >
              <h3 className="font-semibold text-xl text-[#0A192F] mb-6">Request a Discussion</h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                  <Input 
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    className="h-12 bg-slate-50 border-slate-200 focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]"
                    data-testid="contact-name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Work Email *</label>
                  <Input 
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@company.com"
                    className="h-12 bg-slate-50 border-slate-200 focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]"
                    data-testid="contact-email"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company / Institution</label>
                  <Input 
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Your organization"
                    className="h-12 bg-slate-50 border-slate-200 focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]"
                    data-testid="contact-company"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <Input 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    className="h-12 bg-slate-50 border-slate-200 focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]"
                    data-testid="contact-phone"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 bg-[#0A192F] hover:bg-[#2563EB] text-white rounded-full text-base font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Request Discussion
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center pt-2">
                  By submitting, you agree to our privacy policy. We'll never share your information.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A192F] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">F</span>
                </div>
                <span className="font-playfair text-2xl font-bold">FinOrchest</span>
              </div>
              <p className="text-slate-400 max-w-sm mb-6">
                Enterprise fund management reimagined. Built for banks, designed for enterprises.
              </p>
              <p className="text-sm text-slate-500">
                © 2026 FinOrchest Technologies. All rights reserved.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Platform</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Solutions</a></li>
                <li><a href="#future" className="hover:text-white transition-colors">CBDC Ready</a></li>
                <li><a href="/login" className="hover:text-white transition-colors">Sign In</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Company</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#contact" className="hover:text-white transition-colors">Partner With Us</a></li>
                <li><a href="mailto:partnerships@finorchest.com" className="hover:text-white transition-colors">Contact</a></li>
                <li><span className="text-[#D4AF37]">Digital Rupee Ready</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              Made in India 🇮🇳 for the future of finance
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>Privacy Policy</span>
              <span>Terms of Service</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPagePro;
